import { useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Alert, Box, Button, Pagination, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import { InsAuditLog } from '@/core/models/ins-audit.model'
import { Staff } from '@/core/models/staff.model'
import dayjs from 'dayjs'
import { insAuditOperationMessages } from '@/pages/patient/lib/ins-audit-labels'
import { InsAuditResultChip } from './InsAuditResultChip'
import { InsiReturnLogDialog } from './InsiReturnLogDialog'

export type InsAuditLogsTableProps = {
  logs: InsAuditLog[]
  isLoading: boolean
  isError: boolean
  page: number
  pageCount: number
  total: number
  pageSize: number
  onPageChange: (page: number) => void
}

function buildStaffLabel(staff: Staff): string {
  if (!staff) {
    return ''
  }
  return `${staff.userInfo?.surname ?? ''} ${staff.userInfo?.name ?? ''}`.trim()
}

export function InsAuditLogsTable({ logs, isLoading, isError, page, pageCount, total, pageSize, onPageChange }: InsAuditLogsTableProps) {
  const lingui = useLingui()
  const [returnLog, setReturnLog] = useState<InsAuditLog | null>(null)
  const patientUnavailable = t`Patient not available`
  const insIdentityNumberLabel = t`INS identity number`
  const oidLabel = t`OID`
  const emitterLabel = t`Emitter`

  const hasRows = !isLoading && logs.length > 0

  const currentCount = logs.length
  const startIndex = hasRows ? (page - 1) * pageSize + 1 : 0
  const endIndex = hasRows ? startIndex + currentCount - 1 : 0

  if (isError) {
    return (
      <Alert severity="error">
        <Trans>Unable to load INSi traces.</Trans>
      </Alert>
    )
  }

  if (!isLoading && logs.length === 0) {
    return (
      <Typography component="div" color="text.secondary">
        <Trans>No INSi traces for this establishment.</Trans>
      </Typography>
    )
  }

  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <Trans>Date</Trans>
              </TableCell>
              <TableCell>
                <Trans>Staff</Trans>
              </TableCell>
              <TableCell>
                <Trans>Patient identity</Trans>
              </TableCell>
              <TableCell>
                <Trans>Operation</Trans>
              </TableCell>
              <TableCell>
                <Trans>Result</Trans>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log._id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(log.createdAt).isValid() ? dayjs(log.createdAt).format('DD/MM/YYYY HH:mm') : ''}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-line', fontSize: '0.875rem' }}>{buildStaffLabel(log.staff)}</TableCell>
                <TableCell sx={{ minWidth: 300 }}>
                  <Box display="flex" flexDirection="column" gap={0.25}>
                    <Typography variant="body2" fontWeight={600}>
                      {log.patientFullName || patientUnavailable}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {insIdentityNumberLabel}: {log.patientInsNIR || log.returnedIdentity?.insNIR || log.receivedIdentity?.insNIR || ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {oidLabel}: {log.patientInsOID || log.returnedIdentity?.insOID || log.receivedIdentity?.insOID || ''}
                    </Typography>
                    {log.emitter && (
                      <Typography variant="caption" color="text.secondary">
                        {emitterLabel}: {log.emitter}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{lingui._(insAuditOperationMessages[log.operation])}</TableCell>
                <TableCell>
                  <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                    <InsAuditResultChip operation={log.operation} resultCode={log.resultCode} />
                    {(log.returnedIdentity || log.receivedIdentity) && (
                      <Button size="small" variant="text" startIcon={<VisibilityRoundedIcon fontSize="small" />} onClick={() => setReturnLog(log)}>
                        <Trans>View INSi return</Trans>
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {hasRows && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Typography component="div" variant="body2" color="text.secondary">
            <Trans>
              Showing {startIndex}–{endIndex} of {total} results
            </Trans>
          </Typography>

          {pageCount > 1 && (
            <Pagination count={pageCount} page={page} onChange={(_event, newPage) => onPageChange(newPage)} size="small" variant="outlined" shape="rounded" />
          )}
        </Box>
      )}

      {returnLog && <InsiReturnLogDialog open={Boolean(returnLog)} onClose={() => setReturnLog(null)} log={returnLog} />}
    </>
  )
}
